import React, { PropsWithChildren, useContext, useMemo, useState } from "react";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { usePost } from "../../API";
import { ThreeColumnPage } from "../../Components/ThreeColumnPage";
import { useParams } from "react-router-dom";
import { useLocalizedStrings } from "../../Localization";
import { Alert, ChatMessageView, Loading, ResizablePanel } from "@aidkitorg/component-library";
import { AIConversation, ChatMessageItemTypes } from "@aidkitorg/types/lib/convoai";
import { DEFAULT_AGENT_NAME, DEFAULT_AVATAR_URL, MessageContent } from "../AICoachUI";
import { PublicConfigurationContext } from "../../Context";

type Interaction = {
  appState?: ChatMessageItemTypes["appState"],
  userMessages: ChatMessageItemTypes['message'][],
  coachMessage: ChatMessageItemTypes['message'],
};

export type ConversationReviewContextType = {
  enabled: boolean,
  conversation?: AIConversation,
  interactions: Interaction[],
  activeInteraction?: Interaction,
  setConversation: (v: AIConversation) => void,
  setActiveInteraction: (v:Interaction) => void
};

const defaultConversationReviewState : ConversationReviewContextType = {
  enabled: false,
  interactions: [] as Interaction[],
  setConversation: () => console.warn('no conversation review context setup, ignoring setConversation'),
  setActiveInteraction: () => console.warn('no conversation review context setup, ignoring setActiveInteraction')
}
export const ConversationReviewContext = React.createContext<ConversationReviewContextType>(defaultConversationReviewState);
export const ConversationReviewProvider = ({ children }: PropsWithChildren) => {
  const config = useContext(PublicConfigurationContext);
  const [conversation, setConversation] = useState<AIConversation | undefined>();
  const [activeInteraction, setActiveInteraction] = useState<Interaction | undefined>();
  const enabled = !!config?.capabilities?.aiCoach && !config.capabilities.aiCoach.disabled
  
  const interactions: Interaction[] = useMemo(() => {
    const result: Interaction[] = [];
    (conversation?.items ?? []).forEach((item, i, arr) => {
      if (item.type === "message" && item.role === "assistant") {
        const interaction: Interaction = {
          userMessages: [] as ChatMessageItemTypes['message'][],
          coachMessage: item,
        }
        // walk backwards and gather appState and user messages
        while (true) {
          i--;
          const prevItem = arr[i] as ChatMessageItemTypes['message'] | ChatMessageItemTypes["appState"];
          if (!prevItem || (prevItem as ChatMessageItemTypes["message"]).role === "assistant") {
            break;
          }
          else if ((prevItem as ChatMessageItemTypes["message"]).role === "user") {
            interaction.userMessages.unshift(prevItem as ChatMessageItemTypes["message"]);
          }
          else if ((prevItem as ChatMessageItemTypes["appState"]).type === "appState") {
            interaction.appState = prevItem as ChatMessageItemTypes["appState"];
          }
        }
        result.push(interaction);
      }
    });
    return result;
  }, [conversation?.items]);
    
  return <ConversationReviewContext.Provider value={{
    enabled,
    conversation, setConversation,
    interactions,
    activeInteraction, setActiveInteraction
  }}>
    {children}
  </ConversationReviewContext.Provider>;
}

export default function ConversationReviewPage() {   
  return <ConversationReviewProvider>
    <ConversationReviewPageContent />  
  </ConversationReviewProvider>;
}

function ConversationReviewPageContent() {
  const L = useLocalizedStrings();
  const getConversation = usePost('/coach/conversation/read');
  const [loading, setLoading] = useState<boolean>(false);
  const { interactions, setConversation, enabled } = useContext(ConversationReviewContext);
  const { conversationId } = useParams() as Record<string, string>;
    
  useAsyncEffect(async () => {
    try {
      if (!enabled) return;
      setLoading(true);
      const result = await getConversation({ id: conversationId });
      if ("error" in result) { 
        throw new Error((result as any).error);
      }
      setConversation({
        ...result,
        created_at: new Date(result.created_at),
        updated_at: new Date(result.updated_at)
      });
    }
    catch (err: any) {
      console.error(err);
    }
    finally { 
      setLoading(false);
    }
  }, [enabled, conversationId]);
  
  return <ThreeColumnPage
    title={L.ai_coach.dashboard.conversations_review}
    main={<>
      { loading && <Loading /> }
      { enabled
        ? <main className="flex h-full">
          <section className="-ml-6 h-full overflow-y-scroll">
            <div className="flex flex-col divide-y">
              {
                interactions.map((interaction, i) => <ChatInteractionReview key={i} interaction={interaction} />)
              }
            </div>
          </section>
          <ResizablePanel displayDensity="compact" label="Survey state" side="right">
            <SurveyPreviewWithAppState />
          </ResizablePanel>
        </main>
        : <Alert variant="warning" className="my-8">{L.ai_coach.dashboard.ai_conversations_not_enabled}</Alert> }
    </>} />
}

function SurveyPreviewWithAppState() { 
  const { activeInteraction } = useContext(ConversationReviewContext);
  return <div className="px-6">
    {activeInteraction && <pre>{JSON.stringify(activeInteraction.appState, null, 2)}</pre>}
  </div>
}

function ChatInteractionReview(props: { 
  interaction: Interaction, 
}) {
  const config = useContext(PublicConfigurationContext);
  const agentName = config?.capabilities?.aiCoach?.appearance?.name || DEFAULT_AGENT_NAME;
  const avatarUrl = config?.capabilities?.aiCoach?.appearance?.avatarUrl || DEFAULT_AVATAR_URL;
  const { userMessages, coachMessage } = props.interaction;
  const { activeInteraction, setActiveInteraction } = useContext(ConversationReviewContext);
  const active = activeInteraction === props.interaction;
  
  return <div className={`cursor-pointer transition-all hover:bg-gray-50 ${active ? 'bg-gray-50 hover:bg-gray-50' : ''}`} onClick={() => setActiveInteraction(props.interaction)}>
    <div className="-mb-12">
      <ChatMessageView
        transcript={
          [...userMessages, coachMessage].map(item => ({
            role: item.role,
            meta: { time: Date.now(), ...(item.meta ?? {}) },
            content: <MessageContent message={item} />
          }))
        }
        roles={{
          assistant: {
            side: 'left',
            color: 'bg-indigo-100',
            icon: <img src={avatarUrl} alt={agentName} className="rounded-full" />
          },
          user: {
            side: 'right',
            icon: <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center font-semibold text-white">U</div>,
            color: 'bg-gray-100',
          }
        }}
      />      
    </div>
  </div>
};
